import React, { useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { PlatformOptions, CONTENT_FORMATS, platformLabel } from './CampaignForm';

const uid = () => Math.random().toString(36).slice(2, 10);

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

/**
 * Read-only rate card — the public face of it, shown on a creator's profile,
 * on the press page, and beside their campaign application.
 *
 * `card` is the shape the API publishes: {starting_usd, min_budget_usd,
 * accepts_barter, open_to_offers, note, items[]}. Renders nothing at all when
 * there is nothing priced, rather than an empty frame.
 */
export function RateCardView({ card, compact = false }) {
    const { t } = useTranslation();
    if (!card) return null;

    const items = (card.items || []).filter(item => item.is_active !== false);
    if (!items.length && !card.min_budget_usd) return null;

    return (
        <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
            {items.map(item => (
                <div key={item.id || item.title}
                    className={`flex items-start gap-3 rounded-xl border bg-white ${item.is_package
                        ? 'border-primary-200 bg-primary-50/40'
                        : 'border-slate-200'} ${compact ? 'p-2.5' : 'p-3'}`}>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <p className={`font-bold text-slate-800 truncate ${compact ? 'text-xs' : 'text-sm'}`}>
                                {item.title}
                            </p>
                            {item.is_package && (
                                <span className="px-1.5 py-0.5 rounded-md bg-primary-600 text-white text-[9px] font-black uppercase tracking-wide">
                                    {t('rateCard.packageTag')}
                                </span>
                            )}
                        </div>

                        {(item.platform || item.content_format) && (
                            <p className="text-[11px] text-slate-400 mt-0.5">
                                {[item.platform && platformLabel(item.platform),
                                item.content_format && t(`campaigns.format_${item.content_format}`)]
                                    .filter(Boolean).join(' · ')}
                            </p>
                        )}

                        {item.description && !compact && (
                            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.description}</p>
                        )}

                        {item.includes?.length > 0 && (
                            <ul className="flex flex-wrap gap-1 mt-1.5">
                                {item.includes.map((line, index) => (
                                    <li key={index} className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-semibold">
                                        {line}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {(item.turnaround_days > 0 || item.revisions_included > 0) && !compact && (
                            <p className="text-[11px] text-slate-400 mt-1.5 flex flex-wrap gap-x-3">
                                {item.turnaround_days > 0 && (
                                    <span>{t('rateCard.turnaround')}: {item.turnaround_days} {t('campaigns.days')}</span>
                                )}
                                {item.revisions_included > 0 && (
                                    <span>{t('rateCard.revisionsIncluded')}: {item.revisions_included}</span>
                                )}
                            </p>
                        )}
                    </div>

                    <p className={`font-black text-slate-900 tabular-nums flex-shrink-0 ${compact ? 'text-sm' : 'text-base'}`}>
                        {usd(item.price_usd)}
                    </p>
                </div>
            ))}

            {(card.min_budget_usd > 0 || card.accepts_barter || card.open_to_offers) && (
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {card.min_budget_usd > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">
                            {t('rateCard.minBudget')} {usd(card.min_budget_usd)}
                        </span>
                    )}
                    {card.accepts_barter && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold">
                            {t('rateCard.barterOk')}
                        </span>
                    )}
                    {card.open_to_offers && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold">
                            {t('rateCard.openToOffers')}
                        </span>
                    )}
                </div>
            )}

            {card.note && (
                <p className="text-[11px] text-slate-400 italic leading-relaxed pt-0.5">{card.note}</p>
            )}
        </div>
    );
}

function emptyItem() {
    return {
        id: uid(), _new: true, title: '', platform: '', content_format: '',
        description: '', price_usd: '', is_package: false, includes: [],
        turnaround_days: 0, revisions_included: 0, is_active: true,
    };
}

/**
 * The creator's editor. The whole line-up is saved in one PUT — rows without
 * a server id are created, rows the creator deleted are dropped — which is
 * why local ids are marked `_new` and stripped before sending.
 *
 * `registerSave` lets a host that has its own save button (the media kit
 * sheet) fire this one too. Two save buttons in one sheet is a trap: prices
 * typed here and left behind by the footer button would just vanish.
 */
export function RateCardEditor({ card, onSaved, registerSave }) {
    const { t } = useTranslation();
    const [form, setForm] = useState(() => ({
        visible: card?.visible ?? false,
        note: card?.note || '',
        min_budget_usd: card?.min_budget_usd ?? 0,
        accepts_barter: card?.accepts_barter ?? false,
        open_to_offers: card?.open_to_offers ?? true,
        items: (card?.items || []).map(item => ({ ...item, includes: item.includes || [] })),
    }));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    const set = (key, value) => { setSaved(false); setForm(prev => ({ ...prev, [key]: value })); };

    const updateItem = (id, key, value) => set('items',
        form.items.map(item => (item.id === id ? { ...item, [key]: value } : item)));

    const removeItem = (id) => set('items', form.items.filter(item => item.id !== id));

    const moveItem = (index, direction) => {
        const items = [...form.items];
        const target = index + direction;
        if (target < 0 || target >= items.length) return;
        [items[index], items[target]] = [items[target], items[index]];
        set('items', items);
    };

    const save = async () => {
        if (form.items.some(item => !item.title.trim())) {
            setError(t('rateCard.titleRequired'));
            return false;
        }
        setSaving(true);
        setError('');
        try {
            const res = await api.put('/api/rate-card/', {
                ...form,
                min_budget_usd: Number(form.min_budget_usd) || 0,
                items: form.items.map(({ _new, id, ...rest }) => ({
                    ...rest,
                    ...(_new ? {} : { id }),          // a local id would not match any row
                    price_usd: Number(rest.price_usd) || 0,
                })),
            });
            if (res.status === 200) {
                setForm(prev => ({
                    ...prev,
                    items: (res.data.items || []).map(item => ({ ...item, includes: item.includes || [] })),
                }));
                setSaved(true);
                onSaved?.(res.data);
                return true;
            }
            return false;
        } catch (err) {
            setError(err.response?.data?.error === 'creators_only'
                ? t('rateCard.creatorsOnly')
                : t('campaigns.saveError'));
            return false;
        } finally {
            setSaving(false);
        }
    };

    // Re-registered whenever the form changes so the host always holds a
    // closure over the *current* rows rather than the first render's.
    useEffect(() => { registerSave?.(save); });

    return (
        <div className="space-y-4">
            {/* Publish switch. A rate card nobody can see is a draft, so say
                which of the two states it is in rather than just toggling. */}
            <div className={`rounded-xl border p-3 flex items-center justify-between gap-3 transition-colors ${form.visible
                ? 'border-primary-200 bg-primary-50'
                : 'border-slate-200 bg-slate-50'}`}>
                <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800">
                        {form.visible ? t('rateCard.published') : t('rateCard.draft')}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                        {form.visible ? t('rateCard.publishedDesc') : t('rateCard.draftDesc')}
                    </p>
                </div>
                <button type="button" onClick={() => set('visible', !form.visible)}
                    className={`w-11 h-6 rounded-full flex items-center transition px-0.5 flex-shrink-0 ${form.visible ? 'bg-primary-600' : 'bg-slate-300'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${form.visible ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
            </div>

            {/* Priced lines */}
            <div className="space-y-2.5">
                {form.items.map((item, index) => (
                    <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
                        <div className="flex items-center gap-2">
                            <input className="flex-1 bg-transparent text-sm font-bold text-slate-800 placeholder-slate-300 focus:outline-none min-w-0"
                                value={item.title} maxLength={120}
                                placeholder={t('rateCard.itemTitlePlaceholder')}
                                onChange={(e) => updateItem(item.id, 'title', e.target.value)} />
                            <button type="button" onClick={() => moveItem(index, -1)}
                                className="text-slate-300 hover:text-slate-700 px-1" title="↑">↑</button>
                            <button type="button" onClick={() => moveItem(index, 1)}
                                className="text-slate-300 hover:text-slate-700 px-1" title="↓">↓</button>
                            <button type="button" onClick={() => removeItem(item.id)}
                                className="text-red-300 hover:text-red-600 px-1">×</button>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <div className="relative w-28">
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">$</span>
                                <input type="number" min={0} step="1" className="input py-1.5 pl-6 text-sm w-full"
                                    value={item.price_usd}
                                    onChange={(e) => updateItem(item.id, 'price_usd', e.target.value)} />
                            </div>
                            <select className="input py-1.5 text-xs w-auto" value={item.platform || ''}
                                onChange={(e) => updateItem(item.id, 'platform', e.target.value)}>
                                <option value="">{t('campaigns.platform')}</option>
                                <PlatformOptions t={t} />
                            </select>
                            <select className="input py-1.5 text-xs w-auto" value={item.content_format || ''}
                                onChange={(e) => updateItem(item.id, 'content_format', e.target.value)}>
                                <option value="">{t('campaigns.format')}</option>
                                {CONTENT_FORMATS.map(f => <option key={f} value={f}>{t(`campaigns.format_${f}`)}</option>)}
                            </select>
                            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                                <input type="checkbox" checked={item.is_package}
                                    onChange={(e) => updateItem(item.id, 'is_package', e.target.checked)}
                                    className="w-3.5 h-3.5 accent-[#20c9b5]" />
                                {t('rateCard.isPackage')}
                            </label>
                        </div>

                        <input className="input py-1.5 text-xs" value={item.description || ''} maxLength={255}
                            placeholder={t('rateCard.itemDescPlaceholder')}
                            onChange={(e) => updateItem(item.id, 'description', e.target.value)} />

                        {item.is_package && (
                            <input className="input py-1.5 text-xs"
                                value={(item.includes || []).join(', ')}
                                placeholder={t('rateCard.includesPlaceholder')}
                                onChange={(e) => updateItem(item.id, 'includes',
                                    e.target.value.split(',').map(x => x.trim()).filter(Boolean))} />
                        )}

                        <div className="flex flex-wrap items-center gap-3">
                            <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
                                {t('rateCard.turnaround')}
                                <input type="number" min={0} max={365} className="input py-1 text-xs w-16"
                                    value={item.turnaround_days || 0}
                                    onChange={(e) => updateItem(item.id, 'turnaround_days', e.target.value)} />
                            </label>
                            <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
                                {t('rateCard.revisionsIncluded')}
                                <input type="number" min={0} max={10} className="input py-1 text-xs w-16"
                                    value={item.revisions_included || 0}
                                    onChange={(e) => updateItem(item.id, 'revisions_included', e.target.value)} />
                            </label>
                            <label className="flex items-center gap-1.5 text-[11px] text-slate-500 ml-auto cursor-pointer">
                                <input type="checkbox" checked={item.is_active !== false}
                                    onChange={(e) => updateItem(item.id, 'is_active', e.target.checked)}
                                    className="w-3.5 h-3.5 accent-[#20c9b5]" />
                                {t('rateCard.lineActive')}
                            </label>
                        </div>
                    </div>
                ))}
            </div>

            {form.items.length === 0 && (
                <p className="text-xs text-slate-400 italic text-center py-3">{t('rateCard.noItems')}</p>
            )}

            <button type="button" onClick={() => set('items', [...form.items, emptyItem()])}
                className="btn-secondary text-xs py-2 px-3 w-full">
                + {t('rateCard.addItem')}
            </button>

            {/* Terms that apply to the card as a whole */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2.5">
                <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold text-slate-600 flex-1">{t('rateCard.minBudgetLabel')}</label>
                    <div className="relative w-28">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">$</span>
                        <input type="number" min={0} className="input py-1.5 pl-6 text-sm w-full"
                            value={form.min_budget_usd}
                            onChange={(e) => set('min_budget_usd', e.target.value)} />
                    </div>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">{t('rateCard.minBudgetHint')}</p>

                <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <span className="text-xs font-semibold text-slate-600">{t('rateCard.acceptsBarter')}</span>
                    <input type="checkbox" checked={form.accepts_barter}
                        onChange={(e) => set('accepts_barter', e.target.checked)}
                        className="w-4 h-4 accent-[#20c9b5]" />
                </label>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <span className="text-xs font-semibold text-slate-600">{t('rateCard.openToOffersLabel')}</span>
                    <input type="checkbox" checked={form.open_to_offers}
                        onChange={(e) => set('open_to_offers', e.target.checked)}
                        className="w-4 h-4 accent-[#20c9b5]" />
                </label>

                <input className="input py-1.5 text-xs" value={form.note} maxLength={255}
                    placeholder={t('rateCard.notePlaceholder')}
                    onChange={(e) => set('note', e.target.value)} />
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <button type="button" onClick={save} disabled={saving}
                className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2">
                {saving && <span className="spinner-sm" />}
                {saved && !saving ? t('rateCard.savedBtn') : t('rateCard.saveBtn')}
            </button>
        </div>
    );
}

/**
 * The Creator-page panel: loads the card once, then hands it to the editor.
 * Kept separate so the editor itself stays a pure controlled form and can be
 * dropped into the media kit sheet too.
 */
export default function RateCardPanel({ className = '' }) {
    const { t } = useTranslation();
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/api/rate-card/')
            .then(res => setCard(res.data))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    return (
        <section className={`rounded-2xl border border-[#c0c0c0] p-5 bg-[#fafafa] ${className}`}>
            <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                {t('rateCard.title')}
            </h2>
            <p className="text-xs text-slate-400 mb-4 leading-relaxed">{t('rateCard.subtitle')}</p>

            {loading
                ? <div className="flex justify-center py-6"><span className="spinner-sm" /></div>
                : <RateCardEditor card={card} />}
        </section>
    );
}
